/**
 * Keeps the cart drawer the site's only cart surface.
 * Intercepts Skio / native adds to cart and forces any redirects or 
 * drawer openings to wait until the network request completes.
 */
class CartDrawerNavigation {
  static CART_PATH = /^\/cart\/?$/;
  static REDIRECT_FLAG = 'nunona:open-cart-drawer';

  constructor() {
    this.pendingAdds = []; // Cola para rastrear peticiones en curso
    
    this.openDrawer = this.openDrawer.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);

    if (CartDrawerNavigation.CART_PATH.test(location.pathname) && !window.Shopify?.designMode) {
      this.redirectAwayFromCart();
      return;
    }

    // Interceptar tráfico de red (Fetch y XHR) de forma centralizada
    this.interceptFetch();
    this.interceptXHR();
    
    // Interceptar navegación y clics
    this.interceptHistoryAndLocation();
    document.addEventListener('click', this.handleClick, true);
    document.addEventListener('submit', this.handleSubmit, true);

    this.openDrawerIfFlagged();
  }

  isCartUrl(url) {
    if (!url) return false;
    try {
      const parsed = new URL(url, location.origin);
      return parsed.origin === location.origin && CartDrawerNavigation.CART_PATH.test(parsed.pathname);
    } catch {
      return false;
    }
  }

  openDrawer() {
    if (this.pendingAdds.length > 0) {
      Promise.all(this.pendingAdds).then(() => {
        this.refreshCartStateAndOpen();
      });
    } else {
      this.refreshCartStateAndOpen();
    }
  }

  async refreshCartStateAndOpen() {
    try {
      const fetcher = window._origFetch || window.fetch;
      const res = await fetcher('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cartData = await res.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cartData } }));
    } catch (e) {}

    try { window.dispatchEvent(new CustomEvent('init-run')); } catch {}
    try { document.dispatchEvent(new CustomEvent('ig:cart:items-changed')); } catch {}
    try { document.dispatchEvent(new CustomEvent('skio::added-to-cart')); } catch {}

    this.forceCartDrawerLoad(() => {
      this.whenAlpineReady(store => store.openCart());
    });
  }

  interceptFetch() {
    const origFetch = window.fetch.bind(window);
    window._origFetch = origFetch; // Guardamos la original limpia

    window.fetch = (input, init) => {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = (init && init.method) || (typeof input === 'object' && input && input.method) || 'GET';
      const isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url) && /post/i.test(method);

      if (isAdd) {
        // Detectar si estamos en un flujo de "Custom Tools" (Skio/Intelligems)
        const isCustomTools = document.body.classList.contains('custom_tools') || /\/custom-tools\//.test(location.pathname);

        if (isCustomTools) {
          document.body.classList.remove('custom_tools');
          document.body.classList.add('custom_tools-loading');
        }

        let resolver;
        const promise = new Promise(r => resolver = r);
        this.pendingAdds.push(promise);

        const finishAdd = () => {
          setTimeout(() => {
            resolver();
            this.pendingAdds = this.pendingAdds.filter(p => p !== promise);
            
            if (isCustomTools) {
              document.body.classList.add('custom_tools');
              document.body.classList.remove('custom_tools-loading');
              // Disparamos evento para que skio-customizacion limpie los botones
              document.dispatchEvent(new CustomEvent('skio::reset-selection'));
            }

            this.openDrawer();
          }, 600);
        };

        const fetchPromise = origFetch(input, init);
        fetchPromise.then(finishAdd).catch(finishAdd);
        
        // Magia: Si es Skio, nos tragamos la promesa para matar su redirección interna.
        // Si no es Skio (carrito normal), retornamos la promesa original para no romper el theme.
        if (isCustomTools) {
          return new Promise(() => {});
        }

        return fetchPromise;
      }

      return origFetch(input, init);
    };
  }

  interceptXHR() {
    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;
    const self = this;

    XMLHttpRequest.prototype.open = function(method, url) {
      this._isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url) && /post/i.test(method);
      origOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function() {
      if (this._isAdd) {
        let resolver;
        const promise = new Promise(r => resolver = r);
        self.pendingAdds.push(promise);

        const finishAdd = () => {
          setTimeout(() => {
            resolver();
            self.pendingAdds = self.pendingAdds.filter(p => p !== promise);
            self.openDrawer();
          }, 600);
        };

        this.addEventListener('load', finishAdd);
        this.addEventListener('error', finishAdd);
      }
      origSend.apply(this, arguments);
    };
  }

  interceptHistoryAndLocation() {
    try {
      const loc = window.location;
      const origAssign = loc.assign.bind(loc);
      const origReplace = loc.replace.bind(loc);
      loc.assign = (url) => { if (this.isCartUrl(url)) return this.openDrawer(); origAssign(url); };
      loc.replace = (url) => { if (this.isCartUrl(url)) return this.openDrawer(); origReplace(url); };
    } catch {}

    try {
      const origPush = history.pushState.bind(history);
      const origRepl = history.replaceState.bind(history);
      history.pushState = (s, t, url) => { if (this.isCartUrl(url)) return this.openDrawer(); origPush(s, t, url); };
      history.replaceState = (s, t, url) => { if (this.isCartUrl(url)) return this.openDrawer(); origRepl(s, t, url); };
    } catch {}
  }

  handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!/\/cart\/add/.test(action)) return;

    event.preventDefault();
    event.stopPropagation();

    window.fetch('/cart/add.js', {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json' }
    }).catch(console.error);
  }

  handleClick(event) {
    const target = event.target.closest('a, button, [role="button"]');
    if (!target) return;
    if (target.id === 'cart-icon' || target.closest('#cart-icon')) return;
    if (target.name === 'checkout' || target.closest('[name="checkout"]')) return;

    const href = target.getAttribute('href') || target.dataset.href;
    if (!this.isCartUrl(href)) return;

    event.preventDefault();
    event.stopPropagation();
    this.openDrawer();
  }

  forceCartDrawerLoad(callback, attempt = 0) {
    if (document.querySelector('#CartDrawer')) {
      callback();
      return;
    }
    if (attempt === 0 || attempt === 10) {
      document.body.dispatchEvent(new CustomEvent('init-run', { bubbles: true }));
    }
    if (attempt >= 50) return;
    setTimeout(() => this.forceCartDrawerLoad(callback, attempt + 1), 100);
  }

  whenAlpineReady(callback, attempt = 0) {
    const store = window.Alpine?.store?.('xMiniCart');
    if (store) {
      callback(store);
      return;
    }
    if (attempt >= 50) {
      // Fallback si Alpine no carga, forzar click en el icono físico
      document.querySelector('#cart-icon')?.click();
      return;
    }
    setTimeout(() => this.whenAlpineReady(callback, attempt + 1), 100);
  }

  openDrawerIfFlagged() {
    if (!sessionStorage.getItem(CartDrawerNavigation.REDIRECT_FLAG)) return;
    sessionStorage.removeItem(CartDrawerNavigation.REDIRECT_FLAG);
    this.openDrawer();
  }

  redirectAwayFromCart() {
    const hideStyle = document.createElement('style');
    hideStyle.textContent = 'html { visibility: hidden; }';
    document.documentElement.appendChild(hideStyle);

    sessionStorage.setItem(CartDrawerNavigation.REDIRECT_FLAG, '1');

    let destination = '/';
    try {
      if (document.referrer) {
        const referrer = new URL(document.referrer);
        if (referrer.origin === location.origin && !CartDrawerNavigation.CART_PATH.test(referrer.pathname)) {
          destination = document.referrer;
        }
      }
    } catch {}

    location.replace(destination);
  }
}

new CartDrawerNavigation();